import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SidePanel } from "@/components/ui/side-panel";
import {
  createMentor,
  getMentor,
  MentorsKeys,
  updateMentor,
} from "@/features/mentors/api/mentors";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
});

type FormValues = z.infer<typeof formSchema>;

interface NewMentorDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClearMentorSelection: () => void;
  selectedMentorId: string | null;
}

const NewMentorDrawer: React.FC<NewMentorDrawerProps> = ({
  open,
  setOpen,
  onClearMentorSelection,
  selectedMentorId,
}) => {
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  // Get Mentor
  const { data: selectedMentor, isLoading: selectedMentorLoading } = useQuery({
    queryKey: [MentorsKeys.GET_MENTOR, selectedMentorId],
    enabled: !!selectedMentorId,
    queryFn: () => getMentor(selectedMentorId as string),
  });

  // Create Mentor
  const { mutate: createMutation, isPending: createPending } = useMutation({
    mutationFn: createMentor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MentorsKeys.GET_MENTORS] });
      setOpen(false);
      form.reset();
    },
  });

  // Update Mentor
  const { mutate: updateMutation, isPending: updatePending } = useMutation({
    mutationFn: (data: FormValues) =>
      updateMentor(selectedMentorId as string, {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MentorsKeys.GET_MENTORS] });
      onClearMentorSelection();
    },
  });

  // Populate form when editing
  React.useEffect(() => {
    if (selectedMentor) {
      form.reset({
        firstName: selectedMentor.firstName,
        lastName: selectedMentor.lastName,
        email: selectedMentor.email,
      });
    } else {
      form.reset({
        firstName: "",
        lastName: "",
        email: "",
      });
    }
  }, [selectedMentor, form]);

  const onSubmit = (values: FormValues) => {
    if (selectedMentorId) {
      updateMutation(values);
    } else {
      createMutation(values);
    }
  };

  const isPending = createPending || updatePending;

  const footerContent = (
    <div className="flex gap-2 justify-end">
      <Button
        type="button"
        variant="outline"
        onClick={onClearMentorSelection}
        disabled={isPending}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="mentor-form"
        disabled={isPending || !form.formState.isValid}
      >
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {selectedMentorId ? "Saving..." : "Creating..."}
          </>
        ) : (
          <>{selectedMentorId ? "Save" : "Create"}</>
        )}
      </Button>
    </div>
  );

  return (
    <SidePanel
      open={open}
      onOpenChange={onClearMentorSelection}
      title={selectedMentorId ? "Edit Mentor" : "Create New Mentor"}
      subtitle={
        selectedMentorId
          ? "Update mentor information"
          : "Add a new mentor to the system"
      }
      footer={footerContent}
    >
      {selectedMentorLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <Form {...form}>
          <form
            id="mentor-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    First Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter first name"
                      autoComplete="given-name"
                      disabled={selectedMentorLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Last Name <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter last name"
                      autoComplete="family-name"
                      disabled={selectedMentorLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="Enter email address"
                      autoComplete="email"
                      disabled={!!selectedMentorId || selectedMentorLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      )}
    </SidePanel>
  );
};

export default NewMentorDrawer;
